// ============================================================================
// DPI-Aware Layout Templates
// ============================================================================

import type { LayoutDefinition } from "./layout";

// ============================================================================
// DPI Conversion Utilities
// ============================================================================

/**
 * Convert inches to dots based on DPI
 */
function inchesToDots(inches: number, dpi: number): number {
  return Math.round(inches * dpi);
}

/**
 * Convert font size from points to dots based on DPI
 * Standard: 1 point = 1/72 inch
 */
function pointsToDots(points: number, dpi: number): number {
  return Math.round((points / 72) * dpi);
}

/**
 * Convert millimeters to dots based on DPI
 */
function mmToDots(mm: number, dpi: number): number {
  return Math.round((mm / 25.4) * dpi);
}

// ============================================================================
// DPI-Aware Label Settings
// ============================================================================

/**
 * Create DPI-aware label settings for standard label size
 * Physical dimensions: 2.25" x 0.75" with 0.04" margins
 */
export function createLabelSettings(dpi: number): {
  width: number;
  height: number;
  dpi: number;
  marginTop: number;
  marginLeft: number;
  marginRight: number;
  marginBottom: number;
} {
  return {
    width: inchesToDots(2.25, dpi), // Standard label width
    height: inchesToDots(0.75, dpi), // Standard label height
    dpi: dpi,
    marginTop: inchesToDots(0.04, dpi), // Standard margin
    marginLeft: inchesToDots(0.04, dpi), // Standard margin
    marginRight: inchesToDots(0.04, dpi), // Standard margin
    marginBottom: inchesToDots(0.04, dpi), // Standard margin
  };
}

// ============================================================================
// DPI-Aware Layout Factory Functions
// ============================================================================

/**
 * Create specimen label layout for a specific DPI
 * Physical dimensions are preserved across different DPIs
 */
export function createSpecimenLayout(dpi: number): LayoutDefinition {
  return {
    type: "vstack",
    children: [
      {
        type: "hstack",
        spacing: "auto",
        children: [
          {
            type: "vstack",
            padding: pointsToDots(2, dpi),
            children: [
              {
                type: "text",
                content: "{{caseId}}",
                fontSize: pointsToDots(7, dpi),
                maxWidth: inchesToDots(1.2, dpi),
              },
              {
                type: "text",
                content: "{{unosId}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                removeWhenEmpty: true,
              },
              {
                type: "text",
                content: "{{alternateId}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                removeWhenEmpty: true,
                truncate: true,
              },
              {
                type: "text",
                content: "{{orgName}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                truncate: true,
              },
            ],
          },
          {
            type: "vstack",
            alignment: "trailing",
            children: [
              {
                type: "text",
                content: "{{tubeName}}",
                fontSize: pointsToDots(6, dpi),
                alignment: "right",
              },
              {
                type: "text",
                content: "{{collection}}",
                fontSize: pointsToDots(6, dpi),
                alignment: "right",
              },
            ],
          },
        ],
      },

      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      {
        type: "hstack",
        spacing: "auto",
        children: [
          {
            type: "barcode",
            content: "{{barcodeContent}}",
            height: pointsToDots(16, dpi), // ~40 dots at 203 DPI
            barWidth: dpi > 203 ? 3 : 2,
            showText: false,
          },
          {
            type: "vstack",
            alignment: "leading",
            children: [
              {
                type: "text",
                content: "{{accessionId}}",
                fontSize: pointsToDots(7, dpi),
                alignment: "right",
              },
              {
                type: "text",
                content: "{{displayId}}",
                fontSize: pointsToDots(7, dpi),
                alignment: "right",
              },
            ],
          },
        ],
      },
      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      // Custom fields section
      {
        type: "vstack",
        children: [
          {
            type: "text",
            content: "{{customField1}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField1",
            removeWhenEmpty: true,
          },
          {
            type: "text",
            content: "{{customField2}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField2",
            removeWhenEmpty: true,
          },
          {
            type: "text",
            content: "{{customField3}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField3",
            removeWhenEmpty: true,
          },
        ],
      },
      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      {
        type: "hstack",
        children: [
          {
            type: "text",
            // border: true,
            content: "{{tests}}",
            fontSize: pointsToDots(6, dpi),
            maxWidth: inchesToDots(2, dpi),
            multiline: true,
            maxLines: 3,
            lineHeight: 1.1,
          },
        ],
      },
    ],
  };
}

/**
 * Create archive label layout for a specific DPI
 */
export function createArchiveLayout(dpi: number): LayoutDefinition {
  return {
    type: "vstack",
    children: [
      {
        type: "hstack",
        spacing: "auto",
        children: [
          {
            type: "vstack",
            padding: pointsToDots(2, dpi),
            children: [
              {
                type: "text",
                content: "{{caseId}}",
                fontSize: pointsToDots(7, dpi),
                maxWidth: inchesToDots(1.2, dpi),
              },
              {
                type: "text",
                content: "{{unosId}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                removeWhenEmpty: true,
              },
              {
                type: "text",
                content: "{{alternateId}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                removeWhenEmpty: true,
                truncate: true,
              },
              {
                type: "text",
                content: "{{orgName}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                truncate: true,
              },
            ],
          },
          {
            type: "vstack",
            alignment: "trailing",
            children: [
              {
                type: "text",
                content: "{{tubeName}}",
                fontSize: pointsToDots(6, dpi),
                alignment: "right",
              },
              {
                type: "text",
                content: "{{collection}}",
                fontSize: pointsToDots(6, dpi),
                alignment: "right",
              },
            ],
          },
        ],
      },

      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      {
        type: "hstack",
        spacing: "auto",
        children: [
          {
            type: "barcode",
            content: "{{barcodeContent}}",
            height: pointsToDots(16, dpi), // ~40 dots at 203 DPI
            barWidth: dpi > 203 ? 3 : 2,
            showText: false,
          },
          {
            type: "vstack",
            alignment: "leading",
            children: [
              {
                type: "text",
                content: "{{accessionId}}",
                fontSize: pointsToDots(7, dpi),
                alignment: "right",
              },
              {
                type: "text",
                content: "{{displayId}}",
                fontSize: pointsToDots(7, dpi),
                alignment: "right",
              },
            ],
          },
        ],
      },
      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      // Custom fields section
      {
        type: "vstack",
        children: [
          {
            type: "text",
            content: "{{customField1}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField1",
            removeWhenEmpty: true,
          },
          {
            type: "text",
            content: "{{customField2}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField2",
            removeWhenEmpty: true,
          },
          {
            type: "text",
            content: "{{customField3}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField3",
            removeWhenEmpty: true,
          },
        ],
      },
      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      {
        type: "hstack",
        children: [
          {
            type: "text",
            // border: true,
            content: "{{archiveInstructions}}",
            fontSize: pointsToDots(6, dpi),
            maxWidth: inchesToDots(2, dpi),
            multiline: true,
            maxLines: 3,
            lineHeight: 1.1,
          },
        ],
      },
    ],
  };
}

/**
 * Create material label layout for a specific DPI
 */
export function createMaterialLayout(dpi: number): LayoutDefinition {
  return {
    type: "vstack",
    children: [
      {
        type: "hstack",
        spacing: "auto",
        children: [
          {
            type: "vstack",
            children: [
              {
                type: "text",
                content: "{{caseId}}",
                fontSize: pointsToDots(7, dpi),
                maxWidth: inchesToDots(1.2, dpi),
              },
              {
                type: "text",
                content: "{{unosId}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                removeWhenEmpty: true,
              },
              {
                type: "text",
                content: "{{alternateId}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                removeWhenEmpty: true,
                truncate: true,
              },
              {
                type: "text",
                content: "{{orgName}}",
                fontSize: pointsToDots(6, dpi),
                maxWidth: inchesToDots(1.2, dpi),
                truncate: true,
              },
            ],
          },
          {
            type: "vstack",
            alignment: "trailing",
            children: [
              {
                type: "text",
                content: "{{materialCode}}",
                fontSize: pointsToDots(6, dpi),
                alignment: "right",
              },
              {
                type: "text",
                content: "Lot: {{lotNumber}}",
                fontSize: pointsToDots(6, dpi),
                alignment: "right",
              },
            ],
          },
        ],
      },

      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      {
        type: "hstack",
        spacing: "auto",
        children: [
          {
            type: "barcode",
            content: "{{barcodeContent}}",
            height: pointsToDots(16, dpi), // ~40 dots at 203 DPI
            barWidth: 2.5,
            showText: false,
          },
          {
            type: "vstack",
            alignment: "leading",
            children: [
              {
                type: "text",
                content: "{{accessionId}}",
                fontSize: pointsToDots(7, dpi),
                alignment: "right",
              },
              {
                type: "text",
                content: "{{displayId}}",
                fontSize: pointsToDots(7, dpi),
                alignment: "right",
              },
            ],
          },
        ],
      },
      { type: "spacer", minHeight: pointsToDots(6, dpi) },
      // Custom fields section
      {
        type: "vstack",
        children: [
          {
            type: "text",
            content: "{{customField1}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField1",
            maxWidth: inchesToDots(2, dpi),
            truncate: true,
            removeWhenEmpty: true,
          },
          {
            type: "text",
            content: "{{customField2}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField2",
            removeWhenEmpty: true,
            maxWidth: inchesToDots(2, dpi),
            truncate: true,
          },
          {
            type: "text",
            content: "{{customField3}}",
            fontSize: pointsToDots(6, dpi),
            id: "customField3",
            removeWhenEmpty: true,
            maxWidth: inchesToDots(2, dpi),
            truncate: true,
          },
        ],
      },
      { type: "spacer", minHeight: inchesToDots(0.05, dpi) }, // ~6 dots at 203 DPI
      {
        type: "hstack",
        children: [
          {
            type: "text",
            // border: true,
            content: "{{tests}}",
            fontSize: pointsToDots(6, dpi),
            maxWidth: inchesToDots(2, dpi),
            multiline: true,
            maxLines: 3,
            lineHeight: 1.1,
          },
        ],
      },
    ],
  };
}

/**
 * Get the appropriate layout for a specific label type and DPI
 */
export function getLayoutForDPI(
  layoutType: "specimen" | "archive" | "material" | "bordered-example",
  dpi: number,
): LayoutDefinition {
  switch (layoutType) {
    case "specimen":
      return createSpecimenLayout(dpi);
    case "archive":
      return createArchiveLayout(dpi);
    case "material":
      return createMaterialLayout(dpi);
    default:
      throw new Error(`Unknown layout type: ${layoutType}`);
  }
}

// ============================================================================
// Utility Exports
// ============================================================================

export { inchesToDots, mmToDots };
